<script setup lang="ts">
import { NuxtLink } from '#components';
import { fetchTurnoverGames } from '~/adapters/api-minigame-turnover/apostatudo-api-fetch-turnover-games.adapter';
import type {
  MinigameTurnoverRequirement,
  MinigameTurnoverScopeGame,
} from '~/types/minigame-turnover';
import {
  formatTurnoverAmount,
  formatTurnoverDay,
} from '~/utils/apostatudo/turnover-requirement';

// Painel do requisito mínimo de turnover de D-1 (ENG-820). Compartilhado pela
// roletinha e pelo check-in — o core manda o MESMO bloco nas duas, e um painel
// por tela seria a mesma frase pronta para divergir.
//
// A COPY OLHA PARA A FRENTE. A régua é do dia ANTERIOR, que já fechou: dizer
// "faltam R$ 46" seria pedir uma ação impossível. O que o jogador pode fazer é
// apostar HOJE para liberar AMANHÃ, e é isso que o título e a descrição dizem.
// O que ele apostou ontem entra como FATO, e por isso vem DEPOIS da descrição:
// primeiro o que fazer, só então o retrato de ontem.
// A MESMA NORMALIZAÇÃO de imagem do resto do cassino. O core manda a URL e a tela
// escolhe a variante: `normalizeThumbnail` tira `/public`, `/mobile` ou `/ipad` que
// possam vir colados e o resize pede ao Cloudflare exatamente 320x373 — mais leve que a
// capa cheia, e na mesma proporção retrato do card. Montar o `src` na mão aqui era uma
// segunda convenção sobre como se carrega a capa de um jogo.
const { getResponsiveThumbnailSrc } = useResponsiveThumbnail();

const props = withDefaults(
  defineProps<{
    requirement: MinigameTurnoverRequirement;
    progress: number;
    /** O que o requisito libera — entra na descrição ("a roleta libera amanhã"). */
    unlocks?: string;
    /** Verbo do título ("girar", "resgatar"). */
    actionVerb?: string;
    /**
     * `true` remove a caixa própria (borda, fundo, sombra) — usado quando quem
     * desenha o fundo é um VÉU por fora. Duas camadas empilhadas escureceriam o
     * dobro e sumiriam com o que está atrás.
     */
    bare?: boolean;
  }>(),
  { unlocks: 'a roleta', actionVerb: 'girar', bare: false },
);

/**
 * Avisa o minigame de que o jogador está SAINDO para um jogo.
 *
 * Fechar a modal de jogos não basta: o painel do minigame (a roletinha, o check-in) é
 * teleportado para o `body`, então numa navegação interna ele continua montado por cima da
 * rota nova — o jogador abre o Aviator e o check-in segue na frente dele. Quem sabe fechar
 * o minigame é o próprio minigame, e o mesmo evento que o CTA do popup já usa.
 */
const emit = defineEmits<{ navigate: [] }>();

const achievedLabel = computed(() =>
  formatTurnoverAmount(props.requirement.achievedAmount),
);

const minLabel = computed(() =>
  formatTurnoverAmount(props.requirement.minAmount),
);

const dayLabel = computed(() => formatTurnoverDay(props.requirement.day));

// "Qualquer jogo" não se soma a nada: com a regra de jogos desligada, prometer
// "os jogos elegíveis" sugeriria uma lista que não existe.
const anyGame = computed(() => props.requirement.scope.all_games === true);

const categories = computed(() => props.requirement.scope.categories);

const gamesCount = computed(() => props.requirement.scope.gamesCount);

/*
 * DOIS TOTAIS, porque são duas perguntas.
 *
 * `total` é o da CONSULTA ATUAL — com a busca ativa, quantos casaram com o termo. É o que
 * o cabeçalho conta ("1 resultado").
 *
 * `scopeTotal` é o tamanho da CURADORIA, e não muda com a busca. Sem ele, procurar um jogo
 * derrubava o total para o do filtro e o campo de busca sumia no meio da digitação — some
 * a ferramenta justamente quando está sendo usada.
 *
 * Declarados aqui, antes de `scopeCount` e `showSearch`, que os leem: os dois precisam do
 * total da rota porque `gamesCount` conta só os jogos avulsos, e uma curadoria pode ser
 * feita só de categorias.
 */
const total = ref<number | null>(null);
const scopeTotal = ref<number | null>(null);

// O botão só existe quando há o que mostrar: categoria inteira, jogo avulso, ou
// os dois.
const hasScopeToShow = computed(
  () => !anyGame.value && (categories.value.length > 0 || gamesCount.value > 0),
);

/**
 * O resumo do cabeçalho da modal: QUANTOS JOGOS, e só.
 *
 * Categoria é vocabulário de quem monta a promoção, não de quem joga — o jogador não sabe
 * o que é "Novos" nem por que isso decide alguma coisa; ele quer saber em quais jogos
 * apostar. A curadoria por categoria continua existindo e valendo (inclusive para jogos
 * que entrarem nela depois), e é justamente por isso que ela aparece aqui EXPANDIDA nos
 * jogos que a compõem hoje, em vez de virar um rótulo.
 *
 * A contagem vem da ROTA (`total`), nunca do payload da tela: o payload conta só os jogos
 * avulsos, então uma curadoria feita só de categorias respondia zero. Enquanto a primeira
 * página não chega, o número do payload responde.
 */
const scopeCount = computed(() => {
  const games = scopeTotal.value ?? gamesCount.value;

  return games > 0 ? `${games} jogo${games > 1 ? 's' : ''}` : '';
});

// A COPY DO OPERADOR VENCE, quando existe. Texto livre por decisão de produto:
// o que ele escreveu vale ao pé da letra, valor incluído. Em branco, a tela monta
// a frase com o valor e o escopo em mãos — e aí ela nunca fica defasada.
const title = computed(
  () =>
    props.requirement.title ?? `Aposte hoje para ${props.actionVerb} amanhã`,
);

const hasCustomDescription = computed(
  () => props.requirement.description !== null,
);

/*
 * A LISTA DE JOGOS, PAGINADA.
 *
 * Ela não vem mais no payload da tela: a curadoria chega a 500 jogos e a lista
 * inteira viajava em toda carga das telas do jogador para servir a minoria que
 * abre esta modal. Agora sai da rota própria, de 50 em 50, e só quando a modal
 * abre — e a busca vai junto para o servidor, porque filtrar no cliente exigiria
 * ter tudo em mãos, que é exatamente o que deixou de acontecer.
 */
const showGames = ref(false);
const games = ref<MinigameTurnoverScopeGame[]>([]);
const hasMore = ref(false);
const loading = ref(false);
const failed = ref(false);
const search = ref('');

// Busca a partir de uma lista que a justifique: abaixo disso o campo é ruído.
const SEARCH_FROM = 10;

// Pelo total REAL da lista, não pelo do payload: com a curadoria feita de categorias o
// payload traz zero, e o campo de busca sumia sobre uma lista de centenas de jogos.
// Pelo tamanho da CURADORIA, nunca pelo resultado da busca: com o total do filtro o campo
// sumia assim que a busca estreitava a lista — a ferramenta desaparecendo em uso.
const showSearch = computed(
  () => (scopeTotal.value ?? gamesCount.value) >= SEARCH_FROM,
);

const searching = computed(() => search.value.trim() !== '');

const noMatches = computed(
  () => searching.value && !loading.value && games.value.length === 0,
);

// Uma requisição por vez: quem digita rápido cancela a anterior, senão a resposta
// de um termo antigo chega depois e sobrescreve a lista do termo atual.
let inFlight: AbortController | null = null;

async function loadPage(offset: number): Promise<void> {
  const requirementId = props.requirement.scope.requirementId;

  if (requirementId === null) return;

  inFlight?.abort();

  const controller = new AbortController();

  inFlight = controller;

  loading.value = true;
  failed.value = false;

  try {
    const page = await fetchTurnoverGames(requirementId, {
      search: search.value.trim(),
      offset,
      signal: controller.signal,
    });

    // Página 0 troca a lista (busca nova); as seguintes empilham.
    games.value = offset === 0 ? page.games : [...games.value, ...page.games];
    hasMore.value = page.hasMore;
    if (page.total !== null) {
      total.value = page.total;

      // O tamanho da curadoria só é aprendido numa consulta SEM termo.
      if (search.value.trim() === '') scopeTotal.value = page.total;
    }
  } catch {
    // Um cancelamento é o fluxo normal de quem digitou de novo, não uma falha:
    // quem abortou já disparou a busca seguinte e vai pintar o resultado dela.
    if (controller.signal.aborted) return;

    failed.value = true;
  } finally {
    if (inFlight === controller) {
      inFlight = null;
      loading.value = false;
    }
  }
}

function openGames(): void {
  showGames.value = true;
  watchViewport();

  // Reabrir com a lista já em mãos não gasta request nenhum.
  if (games.value.length === 0 && !searching.value) loadPage(0);
}

/*
 * A MODAL ANCORADA NA VIEWPORT VISUAL, que é a única que enxerga o teclado.
 *
 * `dvh` não serve aqui: ele acompanha a barra de endereço do navegador, não o teclado —
 * por padrão o Android redimensiona só a viewport VISUAL, deixando a de layout (e portanto
 * `vh` e `dvh`) do tamanho da tela inteira. O resultado era a modal mantendo a altura de
 * sempre com metade dela atrás do teclado; fechar e reabrir o teclado "consertava" porque
 * aí o navegador rolava a página para trazer o campo focado à vista.
 *
 * `visualViewport` dá a altura e o deslocamento reais do que está à mostra, e é a saída que
 * não exige mexer no `interactive-widget` do meta viewport — que é do site inteiro.
 */
const viewportHeight = ref<number | null>(null);
const viewportTop = ref(0);

function syncViewport(): void {
  const vv = window.visualViewport;

  if (!vv) return;

  viewportHeight.value = vv.height;
  viewportTop.value = vv.offsetTop;
}

/**
 * O estilo do overlay — só existe onde `visualViewport` existe.
 *
 * Por isso o overlay é `inset-x-0 top-0` e não `inset-0`: com `top` E `bottom` fixos a
 * altura passa a ser a da viewport de LAYOUT e um `height` em cima é ignorado. Sem
 * `bottom`, quem manda é a altura — `100dvh` onde não há a API, e a medida real onde há.
 */
const viewportStyle = computed(() =>
  viewportHeight.value === null
    ? undefined
    : { height: `${viewportHeight.value}px`, top: `${viewportTop.value}px` },
);

function watchViewport(): void {
  const vv = window.visualViewport;

  if (!vv) return;

  syncViewport();
  vv.addEventListener('resize', syncViewport);
  // O `scroll` entra junto porque o navegador ROLA a viewport visual para revelar o
  // campo focado: sem ele a modal ficaria na altura certa e no lugar errado.
  vv.addEventListener('scroll', syncViewport);
}

function unwatchViewport(): void {
  const vv = window.visualViewport;

  if (!vv) return;

  vv.removeEventListener('resize', syncViewport);
  vv.removeEventListener('scroll', syncViewport);
  viewportHeight.value = null;
  viewportTop.value = 0;
}

/** Sai para o jogo: fecha a lista E o minigame, nesta ordem. */
function leaveForGame(): void {
  closeGames();
  emit('navigate');
}

function closeGames(): void {
  showGames.value = false;
  unwatchViewport();
  inFlight?.abort();
  inFlight = null;
  loading.value = false;
}

let debounce: ReturnType<typeof setTimeout> | null = null;

watch(search, () => {
  if (!showGames.value) return;

  if (debounce !== null) clearTimeout(debounce);
  debounce = setTimeout(() => loadPage(0), 300);
});

// A ROLAGEM CARREGA O RESTO. Uma sentinela no fim da lista em vez de um botão:
// o gesto de procurar um jogo é rolar, e o `IntersectionObserver` não custa um
// listener de scroll por quadro.
const sentinel = ref<HTMLElement | null>(null);
let observer: IntersectionObserver | null = null;

watch(sentinel, (element) => {
  observer?.disconnect();
  observer = null;

  if (element === null) return;

  observer = new IntersectionObserver((entries) => {
    if (!entries[0]?.isIntersecting) return;
    if (loading.value || !hasMore.value) return;

    loadPage(games.value.length);
  });

  observer.observe(element);
});

onBeforeUnmount(() => {
  unwatchViewport();
  observer?.disconnect();
  inFlight?.abort();
  if (debounce !== null) clearTimeout(debounce);
});
</script>

<template>
  <!-- `pointer-events-none` no bloco de texto e `auto` só nos controles: o véu
       por fora já barra o toque no que está atrás, e aqui isso evita que uma
       seleção acidental de texto atrapalhe o clique no botão. -->
  <div
    class="pointer-events-none flex w-full max-w-[360px] flex-col items-center gap-2.5 text-center"
    :class="
      bare
        ? '[text-shadow:0_1px_6px_rgba(0,0,0,0.6)]'
        : 'rounded-2xl border border-white/10 bg-black/60 px-4 py-3.5 shadow-[0_8px_32px_rgba(0,0,0,0.45)] backdrop-blur-md'
    "
    data-testid="minigame-turnover-gate"
  >
    <p class="text-base leading-tight font-bold text-white">
      {{ title }}
    </p>

    <p class="text-[13px] leading-snug text-white/90">
      <template v-if="hasCustomDescription">
        {{ requirement.description }}
      </template>
      <template v-else-if="anyGame">
        Aposte
        <strong class="font-bold text-[#ffc94a]">{{ minLabel }}</strong> hoje em
        qualquer jogo do cassino e {{ unlocks }} libera amanhã.
      </template>
      <template v-else>
        Aposte
        <strong class="font-bold text-[#ffc94a]">{{ minLabel }}</strong> hoje
        nos jogos elegíveis e {{ unlocks }} libera amanhã.
      </template>
    </p>

    <div class="flex w-full flex-col items-center gap-1">
      <p class="text-xs text-white/70">
        <template v-if="dayLabel">Em {{ dayLabel }} você apostou</template>
        <template v-else>Ontem você apostou</template>
        <strong class="mx-1 font-bold text-white">{{ achievedLabel }}</strong>
        de {{ minLabel }}
      </p>

      <div
        aria-valuemax="100"
        aria-valuemin="0"
        :aria-valuenow="progress"
        class="h-1.5 w-full max-w-[220px] overflow-hidden rounded-full bg-white/10"
        role="progressbar"
      >
        <div
          class="h-full rounded-full bg-[#f5b301]"
          :style="{ width: `${progress}%` }"
        />
      </div>

      <!-- Entre a virada do dia e o fim da rotina, quem decide é ANTEONTEM. Sem
           este aviso o jogador lê uma data que não é a de ontem e conclui que a
           aposta dele não contou. Não promete horário: se a rotina quebrou, isto
           segue verdadeiro, e segue sendo a frase certa. -->
      <p
        v-if="requirement.collecting"
        class="mt-0.5 text-[11px] leading-snug text-[#ffc94a]/90"
      >
        Estamos apurando as apostas de ontem — os números acima são do dia
        anterior a ela.
      </p>
    </div>

    <button
      v-if="hasScopeToShow"
      class="pointer-events-auto inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/10 px-3.5 py-2 text-[11px] font-semibold text-white/90 transition-colors hover:bg-white/20"
      type="button"
      @click="openGames"
    >
      Visualizar jogos elegíveis
      <svg
        class="h-3 w-3 shrink-0"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        viewBox="0 0 24 24"
      >
        <path
          d="m9 18 6-6-6-6"
          stroke-linecap="round"
          stroke-linejoin="round"
        />
      </svg>
    </button>

    <slot name="extra" />
  </div>

  <!-- MODAL dos jogos elegíveis. `Teleport` para o body porque o véu vive dentro
       de painéis com `overflow: clip` e contexto de empilhamento próprio — uma
       modal presa ali sairia cortada e atrás do que deveria cobrir.

       `dvh` E NÃO `vh`: a modal encosta no rodapé no celular, e `vh` é a altura da
       viewport SEM descontar o teclado. Com o teclado aberto a conta ficava maior que
       a tela e o campo de busca caía atrás dele — some o teclado, toque de novo, e
       aí sim ficava certo, porque a segunda medição já pegava a tela encolhida. `dvh`
       acompanha o teclado, então a modal encolhe junto na primeira vez. -->
  <Teleport to="body">
    <Transition name="mtg-fade">
      <div
        v-if="showGames"
        class="mtg-sheet fixed inset-x-0 top-0 z-[10000] flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center"
        :style="viewportStyle"
        @click.self="closeGames"
      >
        <div
          class="mtg-panel flex w-full max-w-[520px] flex-col overflow-hidden rounded-t-2xl border border-white/10 bg-[#0b0b23] sm:rounded-2xl"
        >
          <header class="shrink-0 space-y-3 border-b border-white/10 px-4 py-3">
            <div class="flex items-start justify-between gap-3">
              <div class="min-w-0 text-left">
                <p class="text-sm font-bold text-white">Jogos elegíveis</p>
                <p class="text-[11px] leading-snug text-white/55">
                  <!-- Durante a busca o cabeçalho conta o que está NA TELA: deixar
                       o tamanho do escopo ali faria parecer que a modal escondeu o
                       resto. -->
                  <template v-if="searching">
                    {{ total ?? 0 }} resultado{{ total === 1 ? '' : 's' }}
                  </template>
                  <template v-else>
                    {{ scopeCount }} — apostar neles conta para o requisito
                  </template>
                </p>
              </div>

              <button
                aria-label="Fechar"
                class="shrink-0 rounded-full bg-white/10 p-1.5 text-white/80 transition-colors hover:bg-white/20"
                type="button"
                @click="closeGames"
              >
                <svg
                  class="h-4 w-4"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2.5"
                  viewBox="0 0 24 24"
                >
                  <path
                    d="M18 6 6 18M6 6l12 12"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </button>
            </div>

            <div
              v-if="showSearch"
              class="relative"
            >
              <svg
                class="absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2 text-white/35"
                fill="none"
                stroke="currentColor"
                stroke-width="2.5"
                viewBox="0 0 24 24"
              >
                <circle
                  cx="11"
                  cy="11"
                  r="7"
                />
                <path
                  d="m20 20-3.5-3.5"
                  stroke-linecap="round"
                />
              </svg>
              <input
                v-model="search"
                class="w-full rounded-lg border border-white/10 bg-white/5 py-2 pr-3 pl-9 text-[12px] text-white placeholder:text-white/35 focus:border-white/25 focus:outline-none"
                placeholder="Buscar jogo..."
                type="search"
              />
            </div>
          </header>

          <div class="flex-1 overflow-y-auto px-4 py-3">
            <p
              v-if="failed"
              class="py-6 text-center text-[12px] text-white/50"
            >
              Não foi possível carregar a lista agora.
            </p>

            <p
              v-else-if="noMatches"
              class="py-6 text-center text-[12px] text-white/50"
            >
              Nenhum jogo com esse nome na lista.
            </p>

            <!-- GRADE DE CAPAS, como o resto do cassino. A lista é uma porta de
                 ENTRADA: quem lê "aposte hoje para girar amanhã" tem o caminho
                 mais curto num toque, em vez de fechar a modal e caçar o jogo no
                 catálogo. Três colunas no celular porque agora quem identifica o
                 jogo é a capa, não o nome inteiro. -->
            <ul
              v-if="games.length > 0"
              class="grid grid-cols-3 gap-2 text-left sm:grid-cols-4"
            >
              <li
                v-for="game in games"
                :key="game.id"
              >
                <!-- `NuxtLink` quando há slug, `div` quando não há: um
                     `/games/` sem slug seria um 404 oferecido ao jogador. -->
                <component
                  :is="game.slug ? NuxtLink : 'div'"
                  class="pointer-events-auto group block"
                  :to="
                    game.slug ? `/games/${game.slug.toLowerCase()}` : undefined
                  "
                  @click="game.slug && leaveForGame()"
                >
                  <!-- A proporção vem de uma classe própria, e não de
                       `aspect-square`: o plugin aspect-ratio deste projeto
                       atropela a utilitária, e sem proporção fixa o card SEM capa
                       ficava mais baixo que os vizinhos e a grade saía com
                       buracos. Os números são os do card de jogo do site. -->
                  <div
                    class="mtg-cover relative overflow-hidden rounded-lg bg-white/5"
                  >
                    <img
                      v-if="game.imageUrl"
                      :alt="game.name"
                      class="h-full w-full object-cover transition-transform duration-200 group-hover:scale-105"
                      loading="lazy"
                      :src="getResponsiveThumbnailSrc(game.imageUrl)"
                    />
                    <!-- Sem capa, a inicial em vez de um buraco: o card mantém a
                         grade alinhada e o jogo segue clicável. -->
                    <span
                      v-else
                      class="flex h-full w-full items-center justify-center text-lg font-bold text-white/30"
                    >
                      {{ game.name.charAt(0).toUpperCase() }}
                    </span>
                  </div>
                  <p
                    class="mt-1 line-clamp-2 text-[10px] leading-tight font-medium text-white/80"
                  >
                    {{ game.name }}
                  </p>
                </component>
              </li>
            </ul>

            <!-- A sentinela do scroll infinito. Fora do `v-if` da lista para
                 existir já no primeiro carregamento. -->
            <div
              v-if="hasMore"
              ref="sentinel"
              class="h-8"
            />

            <p
              v-if="loading"
              class="py-3 text-center text-[11px] text-white/40"
            >
              Carregando...
            </p>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
/*
 * A ALTURA PADRÃO do overlay, aqui e não numa utilitária do Tailwind: este projeto liga
 * `important: true` na config, então `h-[100dvh]` sairia com `!important` e venceria o
 * `style` inline que a viewport visual escreve — a modal ficava do tamanho da tela inteira
 * com o teclado aberto, que é exatamente o defeito que a medida real existe para corrigir.
 */
.mtg-sheet {
  height: 100dvh;
}

/*
 * `min(80dvh, 100%)` resolve os dois casos sem heurística nenhuma, porque o `100%` é do
 * overlay — que JÁ é a área visível medida pela viewport visual:
 *
 *   sem teclado   → min(80% da tela, tela inteira)   = 80% da tela, como sempre foi;
 *   com teclado   → min(80% da tela, o que sobrou)   = o que sobrou, sem transbordar.
 *
 * Fora da utilitária pelo mesmo motivo da altura: `important: true` faria `max-h-[80dvh]`
 * ganhar de qualquer ajuste, e a folha continuaria maior que a área visível — subindo para
 * fora da tela em vez de descer atrás do teclado.
 */
.mtg-panel {
  max-height: min(80dvh, 100%);
}

/*
 * A MESMA PROPORÇÃO do card de jogo do resto do site (`MediaApostatudo`, que a
 * expressa como `padding-top: 116%` / `128.74%` no celular). Era quadrada aqui, e
 * capa de cassino é RETRATO: o card saía largo demais e destoava de todas as
 * outras listagens.
 */
.mtg-cover {
  aspect-ratio: 100 / 128.74;
}

@media (min-width: 769px) {
  .mtg-cover {
    aspect-ratio: 100 / 116;
  }
}

.mtg-fade-enter-active,
.mtg-fade-leave-active {
  transition: opacity 0.18s ease;
}

.mtg-fade-enter-from,
.mtg-fade-leave-to {
  opacity: 0;
}
</style>
